/**
 * feedCache — module-level cache for the Discover (SocialFeed) feed.
 *
 * Mirrors the dashboardCache (useHomeDashboard) / savedItemsCache (useSavedItemsData)
 * pattern, but additionally stores the accumulated infinite-scroll page list, the
 * pagination cursor, and the scroll position — so navigating away from Discover and
 * back restores the user exactly where they were instead of refetching page 1 and
 * jumping to the top.
 *
 * Keyed by a composite of tab + every active filter (the same inputs that trigger a
 * reload in useFeedData), so a genuine filter change is a cache MISS (fresh load) while
 * a back-navigation with identical filters is a HIT (instant restore).
 *
 * Entry shape: { items, cursor, hasMore, scrollY, ts }
 */

const feedCache = new Map();
const FEED_TTL_MS = 60_000;

// Build a stable cache key from the feed's tab + filter inputs. Order-independent
// because we name each field explicitly.
export const buildFeedCacheKey = ({
  userId,
  activeTab,
  contentFilter,
  searchQuery,
  activeScenario,
  dateFilterKey,
  regionSlug,
  tourSubtype,
  outingKey,
  mixKey,
  textOnly = false
}) => JSON.stringify({
  u: userId || null,
  t: activeTab || 'for-you',
  c: contentFilter || 'all',
  q: searchQuery?.trim() || null,
  s: activeScenario || null,
  d: dateFilterKey || null,
  r: regionSlug || null,
  ts: tourSubtype || 'all',
  o: outingKey || null,
  m: mixKey || null,
  // A text-only search returns different rows for the same words.
  x: textOnly ? 1 : 0
});

// Returns the cached entry if present AND fresh; otherwise null. A stale entry is
// dropped so the caller does a clean fresh load (we don't SWR the feed — the
// accumulated multi-page list + cursor can't be partially revalidated coherently).
export const getCachedFeed = (key) => {
  if (!key) return null;
  const entry = feedCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > FEED_TTL_MS) {
    feedCache.delete(key);
    return null;
  }
  return entry;
};

// Upsert the feed state for a key. Preserves an existing scrollY unless a new one
// is explicitly provided (item/cursor updates during scroll shouldn't clobber the
// last saved scroll position, and vice-versa).
export const setCachedFeed = (key, partial) => {
  if (!key) return;
  const existing = feedCache.get(key);
  feedCache.set(key, {
    items: partial.items ?? existing?.items ?? [],
    cursor: partial.cursor !== undefined ? partial.cursor : (existing?.cursor ?? null),
    hasMore: partial.hasMore !== undefined ? partial.hasMore : (existing?.hasMore ?? true),
    scrollY: partial.scrollY !== undefined ? partial.scrollY : (existing?.scrollY ?? 0),
    ts: Date.now()
  });
};

// Record only the scroll position for a key, without touching its freshness
// timestamp — saving scroll on navigate-away must not resurrect a stale entry.
export const setCachedFeedScroll = (key, scrollY) => {
  if (!key) return;
  const existing = feedCache.get(key);
  if (!existing) return;
  existing.scrollY = scrollY;
};

// Clears the entire feed cache. Called on mutations / sign-out so the user never
// sees a feed reflecting pre-mutation state on their next visit to Discover.
export const invalidateFeedCache = () => {
  feedCache.clear();
};

export { FEED_TTL_MS };
